import { useState, useEffect } from "react";
import {
  Box,
  Typography,
  IconButton,
  Paper,
  Skeleton,
  Button,
} from "@mui/material";
import { ArrowBack, LocationCity, Add, Edit, Delete } from "@mui/icons-material";
import { useParams, useNavigate } from "react-router-dom";
import { fetchZoneById } from "../../api/ZoneService";
import { fetchCitiesByZone, createCity, updateCity, deleteCity } from "../../api/CityService";
import { toast } from "react-toastify";
import theme from "../../theme/theme";
import { CreateCityDialog } from "../../components/city/CreateCityDialog";
import { EditCityDialog } from "../../components/city/EditCityDialog";
import ConfirmDialog from "../../components/ConfirmDialog";

const ZoneDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [zone, setZone] = useState(null);
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);

  const [openCreate, setOpenCreate] = useState(false);
  const [creating, setCreating] = useState(false);
  const [errors, setErrors] = useState({});

  const [openEdit, setOpenEdit] = useState(false);
  const [editingCity, setEditingCity] = useState(null);
  const [editing, setEditing] = useState(false);
  const [editErrors, setEditErrors] = useState({});

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const handleCreateCity = async (cityData) => {
    setCreating(true);
    setErrors({});
    try {
      await createCity(cityData);
      toast.success("City created successfully");
      setOpenCreate(false);
      loadData();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to create city");
      }
    } finally {
      setCreating(false);
    }
  };

  const handleEditCity = async (cityData) => {
    setEditing(true);
    setEditErrors({});
    try {
      await updateCity(editingCity.id, cityData);
      toast.success("City updated successfully");
      setOpenEdit(false);
      setEditingCity(null);
      loadData();
    } catch (error) {
      if (error?.response?.data?.errors) {
        setEditErrors(error.response.data.errors);
      } else {
        toast.error(error.message || "Failed to update city");
      }
    } finally {
      setEditing(false);
    }
  };

  const handleDeleteClick = (city) => {
    setDeleteTarget(city);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await deleteCity(deleteTarget.id);
      if (res?.status === "error") {
        toast.error(res.message || "Failed to delete city");
      } else {
        toast.success("City deleted successfully");
        setConfirmOpen(false);
        setDeleteTarget(null);
        loadData();
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error.message || "Failed to delete city");
    } finally {
      setDeleting(false);
    }
  };

  const loadData = async () => {
    setLoading(true);
    try {
      const zoneRes = await fetchZoneById(id);
      setZone(zoneRes.data.data ? zoneRes.data.data : zoneRes.data);

      const cityRes = await fetchCitiesByZone(id);
      setCities(cityRes.data.data ? cityRes.data.data : cityRes.data);
    } catch (error) {
      toast.error(error.message || "Failed to fetch Zone Details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate("/zones")}>
          <ArrowBack sx={{ color: theme.palette.primary.main }} />
        </IconButton>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h5" fontWeight="700">
            {loading ? <Skeleton width={150} /> : `${zone?.name} Zone`}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {loading ? <Skeleton width={80} /> : `Code: ${zone?.code}`}
          </Typography>
        </Box>
      </Box>

      <Paper sx={{ p: 4, borderRadius: theme.paper.borderRadius }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography variant="h6" fontWeight="700" color="primary">
            Operating Cities
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenCreate(true)}
          >
            Create City
          </Button>
        </Box>

        {loading ? (
          <Skeleton variant="rounded" height={100} />
        ) : cities.length > 0 ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 2,
            }}
          >
            {cities.map((city) => (
              <Box
                key={city.id}
                sx={{
                  p: 2,
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <LocationCity color="primary" />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body1" fontWeight="600">
                    {city.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    display="block"
                  >
                    Code: {city.code}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="primary"
                    fontWeight="600"
                  >
                    {city.projects_count || 0} Project
                    {city.projects_count !== 1 ? "s" : ""}
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={() => {
                    setEditingCity(city);
                    setEditErrors({});
                    setOpenEdit(true);
                  }}
                  sx={{ color: "primary.main" }}
                  title="Edit City"
                >
                  <Edit fontSize="small" />
                </IconButton>
                <IconButton
                  size="small"
                  onClick={() => handleDeleteClick(city)}
                  sx={{ color: "#ff5252" }}
                  title="Delete City"
                >
                  <Delete fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        ) : (
          <Typography variant="body1" color="text.secondary">
            No cities configured for this zone.
          </Typography>
        )}

        <CreateCityDialog
          open={openCreate}
          onClose={() => setOpenCreate(false)}
          saving={creating}
          onSave={handleCreateCity}
          zoneId={id}
          errors={errors}
        />
        <EditCityDialog
          open={openEdit}
          onClose={() => { setOpenEdit(false); setEditingCity(null); }}
          saving={editing}
          onSave={handleEditCity}
          city={editingCity}
          errors={editErrors}
        />
        <ConfirmDialog
          open={confirmOpen}
          onClose={() => {
            setConfirmOpen(false);
            setDeleteTarget(null);
          }}
          onConfirm={handleConfirmDelete}
          title="Delete City"
          message={`Are you sure you want to delete city "${deleteTarget?.name}"? Cities with associated projects cannot be deleted.`}
          loading={deleting}
        />
      </Paper>
    </Box>
  );
};

export default ZoneDetailPage;
